import React from "react";
import { makeApiIdentifier, nameToApiIdentifier } from "state-trees/src/ApiIdentified";
import type { ShopifyApiVersion } from "state-trees/src/connections/shopify/ShopifyApiVersion";
import {
  eachShopifyModelMetadataForVersionWithModelKey,
  findInheritWebhooksField,
  shopifyModelSpecForVersion,
} from "state-trees/src/connections/shopify/ShopifyModelMetadata";
import { isConditionallyWebhookTriggeredModelKey } from "state-trees/src/connections/shopify/sync-only";
import { unthunk } from "state-trees/src/utils";
import { TD, TH, TR, Table } from "web/src/components/markdown/Table";
import { InlineCode } from "../../markdown-components";

type Row = { modelName: string; parentModelName: string; fieldOnParent: string };

export const ShopifyConditionallyWebhookSyncedTable = (props: { apiVersion: ShopifyApiVersion; frameworkVersionConstraint: string }) => {
  const { apiVersion, frameworkVersionConstraint } = props;
  const rows: Row[] = [];

  for (const [modelKey, metadata] of eachShopifyModelMetadataForVersionWithModelKey(apiVersion, frameworkVersionConstraint)) {
    if (!isConditionallyWebhookTriggeredModelKey(modelKey, apiVersion, frameworkVersionConstraint)) {
      continue;
    }
    const inheritWebhooks =
      metadata.inheritWebhooks && typeof metadata.inheritWebhooks === "function"
        ? unthunk(metadata.inheritWebhooks as (apiVersion: ShopifyApiVersion) => string, apiVersion)
        : metadata.inheritWebhooks;
    const parentKey = metadata.parentKey;
    if (!inheritWebhooks || !parentKey) continue;

    const childSpec = shopifyModelSpecForVersion(modelKey, apiVersion, frameworkVersionConstraint);
    const parentSpec = shopifyModelSpecForVersion(parentKey, apiVersion, frameworkVersionConstraint);
    if (!childSpec || !parentSpec) continue;

    const parentField = findInheritWebhooksField(parentSpec, inheritWebhooks);
    const fieldOnParent = parentField ? makeApiIdentifier(parentField.name, parentField.customApiIdentifier) : inheritWebhooks;

    rows.push({
      modelName: childSpec.name,
      parentModelName: parentSpec.name,
      fieldOnParent,
    });
  }

  rows.sort((a, b) => a.modelName.localeCompare(b.modelName));

  return (
    <Table>
      <TR>
        <TH>Model</TH>
        <TH>Parent model</TH>
        <TH>Field on parent (configure fetch on webhook)</TH>
      </TR>
      {rows.map((row) => (
        <TR key={row.modelName}>
          <TD>
            <InlineCode>{row.modelName}</InlineCode>
          </TD>
          <TD>
            <InlineCode>{row.parentModelName}</InlineCode>
          </TD>
          <TD>
            <InlineCode>{row.fieldOnParent}</InlineCode>
          </TD>
        </TR>
      ))}
    </Table>
  );
};
